import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { invoiceAPI, quoteAPI, jobAPI, unwrapList } from '../../services/api';
import Sparkline from '../ui/Sparkline';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList, CartesianGrid } from 'recharts';
import { ChartTooltip, ChartEmpty } from '../ui/charts';
import { AXIS_STYLE, GRID_STYLE } from '../ui/charts/chartTheme';
import { DELTA_COLORS } from '../ui/charts/chartTheme';
import { formatCurrency } from '../../utils/formatCurrency';
import { parseDate } from '../../utils/formatDate';
import { formatNumber } from '../dashboard/dashboardData';
import PeriodSelector, { getPeriodDates } from '../ui/PeriodSelector';

/**
 * ReportsOverview — Overview tab content (RPT-1 State A).
 * Header row inside body: PeriodSelector (left) + Download▾ (right).
 * Div-3: 4 scan cards
 * Div-4: Hero Revenue Trend chart with Jobs/Salesperson/Customer/Materials slicers
 * Div-5: 3 supporting cards
 *
 * All aggregations respect the selected period preset key (e.g. 'this_month').
 * Library rendering lives in ReportsLibrary.js; ReportsPage routes the outer tab.
 */
export default function ReportsOverview({
  period = 'this_month',
  onPeriodChange,
  onDownloadCSV,
  onDownloadPDF,
  onSelectReport,
}) {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  // 355_ §3: `quotes` was previously read by supportingCards to generate
  // a count-per-month sparkline on the Quote Conversion tile. That tile now
  // ships no sparkline (per operator ruling — a rate curve is not cheaply
  // derivable from raw quotes here), so `quotes` has no current reader. The
  // fetch is retained because the promise is already in the parallel API
  // call and a future Quote Conversion sub-report will want it.
  // eslint-disable-next-line no-unused-vars
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  // Revenue Trend slicer: dimension the chart is bucketed by.
  // 'jobs' → time-series of job count/revenue in the period
  // 'salesperson' → top salespeople by revenue
  // 'customer' → top customers by revenue
  // 'materials' → top materials by cost consumed
  const [revenueDimension, setRevenueDimension] = useState('jobs');

  // Compute period boundaries once per period change; drives every aggregation.
  const periodRange = useMemo(() => getPeriodDates(period), [period]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, quoteRes, jobRes] = await Promise.allSettled([
        invoiceAPI.getInvoices(),
        quoteAPI.getQuotes(),
        jobAPI.getJobs(),
      ]);
      // IDI-141: jobs endpoint now returns `{jobs, has_more, next_cursor}` after
      // the default cap. `Array.isArray(v) ? v : []` would silently empty every
      // jobs KPI on this page. Unwrap explicitly.
      // IDI-216 (item 3, 2026-08-31): /v1/quotes now ALWAYS returns the
      // enveloped shape too (previously bare-array when no `limit` was
      // passed, which this call site relied on) -- unwrap it the same way.
      // Invoices still bare-array; filed as debt for when its wrapper caps.
      if (jobRes.status === 'rejected') console.error('[ReportsOverview] getJobs failed', jobRes.reason);
      const jobsList = jobRes.status === 'fulfilled'
        ? unwrapList(jobRes.value, 'jobs', 'ReportsOverview').list
        : [];
      setInvoices(invRes.status === 'fulfilled' ? (Array.isArray(invRes.value) ? invRes.value : []) : []);
      setQuotes(quoteRes.status === 'fulfilled' ? unwrapList(quoteRes.value, 'quotes', 'ReportsOverview').list : []);
      setJobs(jobsList);
    } catch (err) {
      console.error('Failed to load report data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compute report card data
  const scanCards = useMemo(() => {
    const now = new Date();
    const { start: periodStart, end: periodEnd, priorStart: priorPeriodStart, priorEnd: priorPeriodEnd } = periodRange;

    const itemDate = (item) => item.created_at || item.createdAt || item.date;

    const isInPeriod = (item) => {
      const dateStr = itemDate(item);
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= periodStart && d <= periodEnd;
    };

    const isInPriorPeriod = (item) => {
      const dateStr = itemDate(item);
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= priorPeriodStart && d <= priorPeriodEnd;
    };

    // Q-B3 (2026-08-18, 347_ \u00a73 / \u00a73q): distinguish "prior was 0" from
    // "prior missing". The previous branch returned 100 when prior was
    // nullish AND current > 0 \u2014 that's a fabricated number, not a delta.
    //   - Both current and prior are 0 \u2192 delta 0 (unchanged, a real value)
    //   - Prior is null/undefined \u2192 delta null (unknown, no history yet)
    //   - Prior is 0 but current is non-zero \u2192 delta null (division-by-zero;
    //     rate-of-change is undefined; render "\u2014" not "\u221E%" or 100)
    //   - Both non-zero \u2192 real rounded percent
    // ScanCard's guard (below) reads nullish as "hide the vs prior line."
    const computeDelta = (current, prior) => {
      if (prior == null && current == null) return 0;
      if (prior == null) return null;
      if (prior === 0 && current === 0) return 0;
      if (prior === 0) return null;
      return Math.round(((current - prior) / prior) * 100);
    };

    // Metric helpers moved to module scope (355_ §5.2 — ONE definition).
    // Read `invoiceAmount`, `isPaid`, `marginPct` from module-scope
    // exports so the sparkline reducers and these headline aggregations
    // literally call the same code path.
    const isPastDue = (inv) => {
      const st = (inv.status || '').toLowerCase();
      if (st === 'paid') return false;
      const due = inv.due_date || inv.dueDate;
      if (!due) return false;
      return new Date(due) < now;
    };

    // 1. Average Invoice — mean of all invoices in the current period
    const currentInvoices = invoices.filter(isInPeriod);
    const currentAmounts = currentInvoices.map(invoiceAmount).filter(a => a > 0);
    const avgInvoice = currentAmounts.length ? currentAmounts.reduce((a, b) => a + b, 0) / currentAmounts.length : 0;
    const priorInvoices = invoices.filter(isInPriorPeriod);
    const priorAmounts = priorInvoices.map(invoiceAmount).filter(a => a > 0);
    const priorAvgInvoice = priorAmounts.length ? priorAmounts.reduce((a, b) => a + b, 0) / priorAmounts.length : 0;
    const avgInvoiceDelta = computeDelta(avgInvoice, priorAvgInvoice);

    // 2. Average Margin — mean margin % across current-period jobs (derived from quote margin when available)
    // `marginPct` is now the module-scope export (355_ §5.2 — ONE definition).
    const currentMargins = jobs.filter(isInPeriod).map(marginPct).filter(m => m !== null);
    const avgMargin = currentMargins.length ? currentMargins.reduce((a, b) => a + b, 0) / currentMargins.length : 0;
    const priorMargins = jobs.filter(isInPriorPeriod).map(marginPct).filter(m => m !== null);
    const priorAvgMargin = priorMargins.length ? priorMargins.reduce((a, b) => a + b, 0) / priorMargins.length : 0;
    const avgMarginDelta = computeDelta(avgMargin, priorAvgMargin);

    // 3. Current-Month Total Revenue — sum of paid invoices in the current month
    const currentRevenue = invoices.filter(inv => isInPeriod(inv) && isPaid(inv)).reduce((sum, inv) => sum + invoiceAmount(inv), 0);
    const priorRevenue = invoices.filter(inv => isInPriorPeriod(inv) && isPaid(inv)).reduce((sum, inv) => sum + invoiceAmount(inv), 0);
    const revenueDelta = computeDelta(currentRevenue, priorRevenue);

    // 4. Past-Due Invoices — total $ of unpaid invoices past their due date
    const pastDueTotal = invoices.filter(isPastDue).reduce((sum, inv) => sum + invoiceAmount(inv), 0);
    // Q-B3 (2026-08-18, 348_ Q5): priorPastDueTotal + pastDueDelta both removed.
    // past_due is a STOCK; a period-over-period delta on a stock cannot be
    // computed from a current scalar. The dead computation is deleted rather
    // than left commented — the ruling makes it wrong, not merely unused.
    // Retained the computeDelta call site references for readers scanning for parity.

    return [
      // Q-20 FIX A (2026-08-19, 355_ §3): each sparkline plots its own
      // card's metric, applied per bucket via `buildMonthlyBuckets`. The
      // reducers below mirror the headline formulas above so `avg_margin`'s
      // curve moves when `margin_percent` moves — not when the job COUNT
      // moves with margins held constant (§4 RED-PROOF).
      //
      // §5.2 ("tile === table"): the curve and the number derive from ONE
      // definition, applied over different windows.
      { id: 'avg_invoice',
        title: 'Average Invoice',
        primaryValue: formatCurrency(avgInvoice),
        rawValue: avgInvoice,
        delta: avgInvoiceDelta,
        // MEAN of invoiceAmount(inv) over invoices in bucket, filtered a>0
        // — same filter as `currentAmounts` above. Reducer is module-scope
        // (`reduceAvgInvoice`) so the sparkline and the headline literally
        // call the same code.
        sparkData: finalizeSeries(buildMonthlyBuckets(invoices, reduceAvgInvoice)),
        reportId: 'revenue' },

      { id: 'avg_margin',
        title: 'Average Margin',
        primaryValue: `${Math.round(avgMargin)}%`,
        rawValue: avgMargin,
        delta: avgMarginDelta,
        // MEAN of marginPct(j) over jobs in bucket — EXACTLY the headline
        // formula, applied per bucket. Reducer is module-scope
        // (`reduceAvgMargin`) so the sparkline and the headline literally
        // call the same code. A month with no jobs carrying a margin
        // contributes no point; the Q-20 defect was drawing a jobs-count
        // curve here regardless of the margin values.
        sparkData: finalizeSeries(buildMonthlyBuckets(jobs, reduceAvgMargin)),
        reportId: 'margin_analysis' },

      { id: 'current_revenue',
        title: 'Period Revenue',
        primaryValue: formatCurrency(currentRevenue),
        rawValue: currentRevenue,
        delta: revenueDelta,
        // SUM of invoiceAmount(inv) over PAID invoices in bucket — same
        // isPaid filter as the headline. Reducer is module-scope
        // (`reduceCurrentRevenue`) so the sparkline and the headline
        // literally call the same code.
        sparkData: finalizeSeries(buildMonthlyBuckets(invoices, reduceCurrentRevenue)),
        reportId: 'revenue' },

      // Q-B3 (2026-08-18, 348_ Q5): past_due is a STOCK ("how much is
      // sitting past due now") not a FLOW. A period-over-period delta on
      // a stock cannot be computed from a current scalar; the delta field
      // is deliberately absent. ScanCard's guard renders no "vs prior" line.
      //
      // 355_ §3: for the SAME REASON, past_due gets NO sparkData. A
      // per-bucket history cannot be derived from a current-status scalar.
      // Passing no field lets the render-site optional-chain guard
      // (`card.sparkData?.length > 1`) suppress the sparkline entirely.
      { id: 'past_due',
        title: 'Past-Due Invoices',
        primaryValue: formatCurrency(pastDueTotal),
        rawValue: pastDueTotal,
        /* delta omitted per stock ruling */
        invertDelta: true,
        /* sparkData omitted per stock ruling (355_ §3) */
        reportId: 'revenue' },
    ];
  }, [jobs, invoices, periodRange]);

  // Supporting cards: Quote Conversion, Job Completion, Margin.
  //
  // 355_ §3 ruling: these three tiles are NAVIGATION cards for sub-reports;
  // they carry no headline number, so there is no headline formula to
  // mirror in a sparkline. Per Portica's principle "if the rate is not
  // cheaply derivable, pass NOTHING. A missing curve is honest; a count
  // curve under a rate is not" — we pass no `sparkData` at all. The
  // render-site optional-chain guard (`card.sparkData?.length > 1`)
  // suppresses the sparkline. Users still see title + description + the
  // Explore link, which is the actual affordance on these tiles.
  const supportingCards = useMemo(() => [
    { id: 'quote_conversion', title: 'Quote Conversion', description: 'Conversion rate from quote to job', chartType: 'donut', reportId: 'quote_performance' /* no sparkData — 355_ §3 */ },
    { id: 'job_completion',   title: 'Job Completion',   description: 'Job completion trend over time',   chartType: 'trend', reportId: 'job_performance'  /* no sparkData — 355_ §3 */ },
    { id: 'margin',           title: 'Margin',           description: 'Profit margin analysis',           chartType: 'trend', reportId: 'margin_analysis'  /* no sparkData — 355_ §3 */ },
  ], []);

  // Revenue card for hero (uses the Current-Month Revenue scan card as the hero summary)
  const revenueCard = scanCards.find(r => r.id === 'current_revenue');

  // Hero chart data — revenue grouped by the selected dimension.
  // All four branches return [{ name, value }] where value is dollars:
  //   'jobs'         → top 8 jobs by revenue in period
  //   'salesperson'  → top 8 salespeople by invoice revenue in period
  //   'customer'     → top 8 customers by invoice revenue in period
  //   'materials'    → top 8 materials by cost consumed in period
  //
  // Data lookups tolerate common field aliases (snake_case + camelCase).
  const heroChartData = useMemo(() => {
    const { start: pStart, end: pEnd } = periodRange;

    // ---- helpers ----
    const invDate = (inv) => inv.paid_date || inv.paidDate || inv.created_at || inv.createdAt;
    // Field-name check mirrors scan-card behavior: the deployed schema uses
    // any of `amount`, `total_amount`, `totalAmount`, or `total`. Using this
    // wider fallback is what makes the chart actually render on real data.
    const invAmount = (inv) => Number(inv.amount) || Number(inv.total_amount)
      || Number(inv.totalAmount) || Number(inv.total) || 0;
    // Include any non-void, non-draft invoice as "revenue in period". Restricting
    // to paid-only left the chart empty for contractors who have sent invoices
    // that haven't cleared payment yet (still real revenue for reporting purposes).
    const isBillable = (inv) => {
      const s = (inv.status || '').toLowerCase();
      return s !== 'draft' && s !== 'void' && s !== 'voided' && s !== 'cancelled';
    };
    const jobDate = (j) => j.completed_at || j.completedAt || j.updated_at || j.updatedAt || j.created_at || j.createdAt;
    const jobInRange = (j, s, e) => {
      const ds = jobDate(j); if (!ds) return false;
      const d = new Date(ds); return d >= s && d <= e;
    };
    const invInRange = (i, s, e) => {
      if (!isBillable(i)) return false;
      const ds = invDate(i); if (!ds) return false;
      // IDI-518: a bare paidDate is that local day (parseDate), not UTC.
      const d = parseDate(ds); return !!d && d >= s && d <= e;
    };
    const topN = (map, n = 15) => Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, n);

    // ---- Jobs: revenue per job (top N by revenue in period) ----
    // Revenue-per-job is derived from the strongest signal available:
    //   1. Sum of invoice amounts joined by invoice.job_id → job.id
    //   2. If none of the joined invoices carry non-zero amounts (fixture
    //      data / early-stage tenant), fall back to job-level fields:
    //      total / quoted_amount / estimate_total / contract_value.
    // We keep any job that has EITHER a non-zero derived value OR an
    // invoice link that landed in-period (so the chart isn't empty when
    // real work exists but only some fields are populated).
    if (revenueDimension === 'jobs') {
      const jobLabel = (j) => {
        if (!j) return null;
        return j.job_number || j.jobNumber || j.number
          || j.name || j.title
          || `Job ${String(j.id).substring(0, 6)}`;
      };
      const jobValueFallback = (j) => {
        return Number(j.total) || Number(j.revenue) || Number(j.amount)
          || Number(j.quoted_amount) || Number(j.quotedAmount)
          || Number(j.estimate_total) || Number(j.estimateTotal)
          || Number(j.contract_value) || Number(j.contractValue)
          || 0;
      };

      // Step 1: sum invoices by job_id (only in-period, billable).
      const revenueByJobId = new Map();
      for (const inv of invoices) {
        if (!invInRange(inv, pStart, pEnd)) continue;
        const jobId = inv.job_id || inv.jobId;
        if (jobId == null) continue;
        revenueByJobId.set(jobId, (revenueByJobId.get(jobId) || 0) + invAmount(inv));
      }

      // Step 2: build entries. For any job in the period, prefer invoice-sum
      // when non-zero, otherwise fall back to a job-level revenue field.
      // IDI-396: include a job if it is in-period OR has in-period
      // invoices. Previously, a job whose own date was out-of-range but
      // whose invoices were in-range was skipped, leaving the chart
      // empty ("No data in this range").
      const entries = [];
      for (const j of jobs) {
        const hasInRangeInvoices = revenueByJobId.has(j.id);
        if (!jobInRange(j, pStart, pEnd) && !hasInRangeInvoices) continue;
        const invSum = revenueByJobId.get(j.id) || 0;
        const value = invSum > 0 ? invSum : jobValueFallback(j);
        if (value <= 0) continue;
        entries.push({ name: jobLabel(j), value });
      }

      // Step 3: also include any invoice-only jobs (invoice.job_id points to a
      // job outside our fetched jobs[] array — e.g., archived or across pages).
      const knownIds = new Set(jobs.map((j) => j.id));
      for (const [jobId, sum] of revenueByJobId) {
        if (knownIds.has(jobId) || sum <= 0) continue;
        entries.push({ name: `Job ${String(jobId).substring(0, 6)}`, value: sum });
      }

      return entries.sort((a, b) => b.value - a.value).slice(0, 15);
    }

    // ---- Salesperson: revenue by salesperson from invoices in period ----
    if (revenueDimension === 'salesperson') {
      const tally = new Map();
      for (const inv of invoices) {
        if (!invInRange(inv, pStart, pEnd)) continue;
        const name = inv.salesperson_name || inv.salespersonName || inv.salesperson
          || inv.owner_name || inv.ownerName || 'Unassigned';
        tally.set(name, (tally.get(name) || 0) + invAmount(inv));
      }
      return topN(tally);
    }

    // ---- Customer: revenue by customer from invoices in period ----
    if (revenueDimension === 'customer') {
      const tally = new Map();
      for (const inv of invoices) {
        if (!invInRange(inv, pStart, pEnd)) continue;
        const name = inv.customer_name || inv.customerName
          || (inv.customer && (inv.customer.name || inv.customer.display_name))
          || 'Unknown';
        tally.set(name, (tally.get(name) || 0) + invAmount(inv));
      }
      return topN(tally);
    }

    // ---- Materials: cost consumed per material across jobs in period ----
    if (revenueDimension === 'materials') {
      const tally = new Map();
      const add = (name, cost) => {
        if (!name) return;
        const key = String(name).trim();
        if (!key) return;
        tally.set(key, (tally.get(key) || 0) + (Number(cost) || 0));
      };
      for (const job of jobs) {
        if (!jobInRange(job, pStart, pEnd)) continue;
        const lineItems = job.line_items || job.lineItems || [];
        for (const li of lineItems) {
          const mats = li.materials || [];
          for (const m of mats) {
            const name = m.name || m.material_name || m.materialName || m.description;
            const qty = Number(m.quantity || m.qty) || 1;
            const unitCost = Number(m.cost) || Number(m.unit_cost) || Number(m.unitCost) || 0;
            add(name, unitCost * qty);
          }
          if (li.material_only || li.materialOnly) {
            const name = li.name || li.description;
            const qty = Number(li.quantity || li.qty) || 1;
            const unitCost = Number(li.unit_cost) || Number(li.unitCost) || Number(li.cost) || 0;
            add(name, unitCost * qty);
          }
        }
        for (const m of (job.materials || [])) {
          const name = m.name || m.material_name || m.materialName || m.description;
          const qty = Number(m.quantity || m.qty) || 1;
          const unitCost = Number(m.cost) || Number(m.unit_cost) || Number(m.unitCost) || 0;
          add(name, unitCost * qty);
        }
      }
      return topN(tally);
    }

    return [];
  }, [invoices, jobs, revenueDimension, periodRange]);

  // Material Usage — quantity + cost of specific materials consumed in the period.
  // Distinct from the 'materials' slicer on the Revenue Trend hero: that slicer
  // shows revenue by material (cost), this card tracks USAGE (quantity + cost),
  // grouped by specific material name. Data walked from three sources:
  //   1. job.line_items[].materials[]  — nested per line-item
  //   2. job.line_items[] with material_only=true  — line-item as material
  //   3. job.materials[]  — job-level materials array
  const materialUsageData = useMemo(() => {
    const { start: pStart, end: pEnd } = periodRange;
    const jobDate = (j) => j.completed_at || j.completedAt || j.updated_at || j.updatedAt || j.created_at || j.createdAt;
    const jobInRange = (j) => {
      const ds = jobDate(j); if (!ds) return false;
      const d = new Date(ds); return d >= pStart && d <= pEnd;
    };
    const tally = new Map(); // name -> { name, quantity, cost }
    const add = (name, qty, cost) => {
      if (!name) return;
      const key = String(name).trim();
      if (!key) return;
      const existing = tally.get(key) || { name: key, quantity: 0, cost: 0 };
      existing.quantity += Number(qty) || 0;
      existing.cost += Number(cost) || 0;
      tally.set(key, existing);
    };

    for (const job of jobs) {
      if (!jobInRange(job)) continue;
      const lineItems = job.line_items || job.lineItems || [];
      for (const li of lineItems) {
        for (const m of (li.materials || [])) {
          const name = m.name || m.material_name || m.materialName || m.description;
          const qty = Number(m.quantity || m.qty) || 1;
          const unitCost = Number(m.cost) || Number(m.unit_cost) || Number(m.unitCost) || 0;
          add(name, qty, unitCost * qty);
        }
        if (li.material_only || li.materialOnly) {
          const name = li.name || li.description;
          const qty = Number(li.quantity || li.qty) || 1;
          const unitCost = Number(li.unit_cost) || Number(li.unitCost) || Number(li.cost) || 0;
          add(name, qty, unitCost * qty);
        }
      }
      for (const m of (job.materials || [])) {
        const name = m.name || m.material_name || m.materialName || m.description;
        const qty = Number(m.quantity || m.qty) || 1;
        const unitCost = Number(m.cost) || Number(m.unit_cost) || Number(m.unitCost) || 0;
        add(name, qty, unitCost * qty);
      }
    }

    return Array.from(tally.values())
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 8);
  }, [jobs, periodRange]);

  return (
    <div>
      <OverviewLayout
        scanCards={scanCards}
        revenueCard={revenueCard}
        heroChartData={heroChartData}
        revenueDimension={revenueDimension}
        onRevenueDimensionChange={setRevenueDimension}
        materialUsageData={materialUsageData}
        supportingCards={supportingCards}
        loading={loading}
        onSelectReport={onSelectReport}
        period={period}
        onPeriodChange={onPeriodChange}
        onDownloadCSV={onDownloadCSV}
        onDownloadPDF={onDownloadPDF}
      />
    </div>
  );
}

function OverviewLayout({
  scanCards,
  revenueCard,
  heroChartData,
  revenueDimension,
  onRevenueDimensionChange,
  materialUsageData,
  supportingCards,
  loading,
  onSelectReport,
  period,
  onPeriodChange,
  onDownloadCSV,
  onDownloadPDF,
}) {
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[1, 2, 3, 4].map(i => <div key={i} style={skeletonCardStyle}><div style={{ ...shimmerBlock, height: 60 }} /></div>)}
        </div>
        <div style={skeletonCardStyle}><div style={{ ...shimmerBlock, height: 180 }} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[1, 2, 3].map(i => <div key={i} style={skeletonCardStyle}><div style={{ ...shimmerBlock, height: 60 }} /></div>)}
        </div>
      </div>
    );
  }

  const dimensionOptions = [
    { id: 'jobs',        label: 'Jobs' },
    { id: 'salesperson', label: 'Salesperson' },
    { id: 'customer',    label: 'Customer' },
    { id: 'materials',   label: 'Materials' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header row scoped to the Overview panel: date picker left, download right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <PeriodSelector value={period} onChange={onPeriodChange} />
        {/* PR-24 post-verify (300_ §3): Overview Download hidden until
            server-side export is wired. Individual reports keep their own Download. */}
      </div>

      {/* Div-3: Scan cards (4 in a row) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {scanCards.map(card => (
          <ScanCard key={card.id} card={card} onClick={() => onSelectReport(card.reportId || card.id)} />
        ))}
      </div>

      {/* Div-4: Hero Revenue chart */}
      {revenueCard && (
        <div
          onClick={() => onSelectReport('revenue')}
          style={heroCardStyle}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text-default)' }}>
                Revenue Trend
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
                Current period
              </p>
              {/* RPT-1 State A: metric slicer (Revenue / Profit / Jobs) */}
              <div
                role="tablist"
                aria-label="Revenue Trend metric"
                onClick={(e) => e.stopPropagation()}
                style={metricSlicerStyle}
              >
                {dimensionOptions.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={revenueDimension === m.id}
                    onClick={() => onRevenueDimensionChange && onRevenueDimensionChange(m.id)}
                    style={{
                      ...metricSlicerBtnStyle,
                      ...(revenueDimension === m.id ? metricSlicerBtnActiveStyle : {}),
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--color-text-default)' }}>
                {revenueCard.primaryValue}
              </p>
              <DeltaBadge delta={revenueCard.delta} />
            </div>
          </div>
          {/* Hero BarChart — revenue by the selected dimension (MD2 spec).
              - Position-on-common-scale encoding (Cleveland/McGill best rank)
              - Direct value labels at bar-end (no always-on axis clutter)
              - Two implicit reference gridlines instead of full lattice
              - ChartTooltip: series-dot + name + value in MD2 tooltip shape
              - Row height 40px so labels & values breathe
              - Empty state via ChartEmpty (consistent copy + optional CTA) */}
          <div style={{ marginTop: 20 }}>
            {heroChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(240, heroChartData.length * 40)}>
                <BarChart
                  data={heroChartData}
                  layout="vertical"
                  margin={{ top: 8, right: 72, bottom: 8, left: 8 }}
                  barCategoryGap="28%"
                >
                  <CartesianGrid
                    horizontal={false}
                    stroke={GRID_STYLE.stroke}
                    strokeDasharray={GRID_STYLE.strokeDasharray}
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    tick={AXIS_STYLE.tick}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--neutral-100)' }}
                    content={<ChartTooltip formatter={(v) => formatCurrency(v)} />}
                  />
                  <Bar
                    dataKey="value"
                    fill="var(--color-action-primary)"
                    radius={[0, 4, 4, 0]}
                    name={`Revenue by ${revenueDimension}`}
                  >
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={(v) => formatCurrency(v)}
                      style={{ fill: 'var(--color-text-default)', fontSize: 12, fontWeight: 600 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty
                entityName={revenueDimension === 'materials' ? 'material usage' : 'revenue'}
                chartType="bar"
                inline
              />
            )}
          </div>
        </div>
      )}

      {/* Div-4b: Material Usage — quantity + cost of specific materials consumed */}
      <div style={materialUsageCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-default)' }}>
              Material Usage
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
              Top materials by quantity consumed across jobs in period
            </p>
          </div>
        </div>
        {materialUsageData && materialUsageData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(240, materialUsageData.length * 40)}>
            <BarChart
              data={materialUsageData}
              layout="vertical"
              margin={{ top: 8, right: 72, bottom: 8, left: 8 }}
              barCategoryGap="28%"
            >
              <CartesianGrid horizontal={false} stroke={GRID_STYLE.stroke} strokeDasharray={GRID_STYLE.strokeDasharray} />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={160}
                tick={AXIS_STYLE.tick}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <Tooltip
                cursor={{ fill: 'var(--neutral-100)' }}
                content={<ChartTooltip formatter={(val) => formatNumber(val)} />}
              />
              <Bar dataKey="quantity" fill="var(--color-action-primary)" radius={[0, 4, 4, 0]} name="Quantity used">
                <LabelList
                  dataKey="quantity"
                  position="right"
                  formatter={(v) => formatNumber(v)}
                  style={{ fill: 'var(--color-text-default)', fontSize: 12, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartEmpty entityName="material usage" chartType="bar" inline />
        )}
      </div>

      {/* Div-5: Supporting cards (3 in a row) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        {supportingCards.map(card => (
          <SupportingCard key={card.id} card={card} onClick={() => onSelectReport(card.reportId || card.id)} />
        ))}
      </div>
    </div>
  );
}

function ScanCard({ card, onClick }) {
  // Q-B3 (2026-08-18, 347_ §3): nullish-not-falsy guard on card.delta.
  // delta === 0 is a REAL value ("unchanged period over period") and MUST
  // render as "0%". null/undefined = hide the whole "vs prior period" line.
  // Matches PR-46's KPIRow contract. `past_due` (stock) omits delta entirely.
  const hasDelta = card.delta != null;
  const deltaColor = !hasDelta ? null
    : card.delta === 0 ? DELTA_COLORS.neutral
    : card.delta > 0 ? DELTA_COLORS.positive
    : DELTA_COLORS.negative;
  const deltaArrow = !hasDelta ? null
    : card.delta > 0 ? '▲'
    : card.delta < 0 ? '▼'
    : '–';

  return (
    <div
      onClick={onClick}
      style={scanCardStyle}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-action-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--neutral-200)'; }}
    >
      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
        {card.title}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--color-text-default)' }}>
          {card.primaryValue}
        </p>
        {card.sparkData?.length > 1 && (
          <Sparkline data={card.sparkData} height={28} width={56} />
        )}
      </div>
      {hasDelta && (
        <p style={{ margin: '6px 0 0', fontSize: 11, color: deltaColor }}>
          {deltaArrow} {Math.abs(card.delta)}% vs prior period
        </p>
      )}
    </div>
  );
}

function SupportingCard({ card, onClick }) {
  return (
    <div
      onClick={onClick}
      style={supportingCardStyle}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-action-primary)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--neutral-200)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-text-default)' }}>
          {card.title}
        </p>
        {card.sparkData?.length > 1 && (
          <Sparkline data={card.sparkData} height={24} width={48} />
        )}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
        {card.description}
      </p>
      <span style={exploreLinkStyle}>Explore →</span>
    </div>
  );
}

function DeltaBadge({ delta }) {
  const color = delta === 0 ? DELTA_COLORS.neutral : delta > 0 ? DELTA_COLORS.positive : DELTA_COLORS.negative;
  // Q-B3 (2026-08-18, 347_ \u00a73): nullish-not-falsy guard on delta.
  // delta === 0 is a REAL value ("unchanged period over period") and MUST
  // render as "0%", not be hidden as absent. null/undefined = hide the
  // whole "vs prior period" line. Matches PR-46's KPIRow contract.
  const hasDelta = delta != null;
  const arrow = !hasDelta ? null : delta > 0 ? '▲' : delta < 0 ? '▼' : '–';
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color }}>
      {hasDelta ? `${arrow} ${Math.abs(delta)}%` : null}
    </span>
  );
}

// ─── Metric definitions ────────────────────────────────────────────────────────────────────────────
// Q-20 FIX A (2026-08-19, 355_ §5.2): these are the ONE definitions for
// each metric. Both the headline aggregations inside `scanCards` and the
// sparkline per-bucket reducers below MUST call these helpers — do not
// inline the formulas anywhere else. That's what makes the curve and the
// number agree “by construction.”
//
// N-14 aliases (355_ §4 note): marginPct ORs three spellings
// (`margin_percent` · `marginPercent` · `margin_pct`). Canonical spelling
// per N-14 (writer emits): `margin_percent`. Others kept as legacy readers.
export function invoiceAmount(inv) {
  return Number(inv.amount ?? inv.total_amount ?? inv.totalAmount ?? inv.total) || 0;
}
export function isPaid(inv) {
  const st = (inv.status || '').toLowerCase();
  return st === 'paid';
}
export function marginPct(j) {
  const m = Number(j.margin_percent || j.marginPercent || j.margin_pct);
  return isNaN(m) ? null : m;
}

// ─── Per-card reducers ────────────────────────────────────────────────
// Each reducer receives the items falling in one monthly bucket and
// returns the metric value FOR THAT BUCKET, or `null` if the metric is
// undefined for the window (355_ §5.2 — "no data → no line", per-bucket).
export function reduceAvgInvoice(monthInvoices) {
  const amounts = monthInvoices.map(invoiceAmount).filter(a => a > 0);
  if (amounts.length === 0) return null;
  return amounts.reduce((a, b) => a + b, 0) / amounts.length;
}
export function reduceAvgMargin(monthJobs) {
  const ms = monthJobs.map(marginPct).filter(m => m !== null);
  if (ms.length === 0) return null;
  return ms.reduce((a, b) => a + b, 0) / ms.length;
}
export function reduceCurrentRevenue(monthInvoices) {
  const paid = monthInvoices.filter(isPaid);
  if (paid.length === 0) return null;
  return paid.reduce((sum, inv) => sum + invoiceAmount(inv), 0);
}

// ─── Sparkline series helpers ────────────────────────────────────────────
// Q-20 (2026-08-19, 355_): each card's sparkline plots the SAME formula
// as its own headline number, applied per bucket. Prior behaviour called
// `generateSparklineData(rows, kind)` which ignored `kind` and always
// produced a row-count series — an unrelated quantity under every
// headline. §5.2 ("tile === table"): the curve and the number must be
// computed by ONE definition, applied over different windows.
//
// resolveItemDate: SAME resolver used by `scanCards.itemDate` above
// (§3.1 — the sparkline and the headline must agree on which bucket a
// record is in, and the old inline `... || updated_at || updatedAt`
// fallback put stale records in fresh buckets).
export function resolveItemDate(item) {
  return (item && (item.created_at || item.createdAt || item.date)) || null;
}

// buildMonthlyBuckets: partitions `items` into six monthly windows
// (oldest → newest) and hands each window to `reducer(monthItems, {start, end})`.
// The reducer returns the metric VALUE for that bucket (a number) or `null`
// if the metric is undefined for the window (e.g. no jobs closed → no mean
// margin). Nulls are dropped by the caller if they want a compact series.
//
// Returns an array of exactly `MONTHLY_BUCKETS_N` points shaped `{ value }`
// where `value` may be a number or `null`. Callers should decide how to
// handle nulls — Sparkline reads only numeric values.
export const MONTHLY_BUCKETS_N = 6;
export function buildMonthlyBuckets(items, reducer) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const now = new Date();
  const out = [];
  for (let i = MONTHLY_BUCKETS_N - 1; i >= 0; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
    const monthItems = items.filter(item => {
      const dateStr = resolveItemDate(item);
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= start && d <= end;
    });
    const value = reducer(monthItems, { start, end });
    out.push({ value });
  }
  return out;
}

// finalizeSeries: turn a `[{value: number|null}, ...]` array into the
// shape Sparkline expects. Drops null-valued points so a metric that is
// UNDEFINED for a window doesn't render as zero (which is a lie — a real
// zero and "we have no data for this month" are different states, per
// the §Q-20 ruling "no data → no line").
//
// Also enforces the two other §Q-20 rulings, without regressing them:
//   • empty input array → empty series (respects Sparkline's `.length > 1`
//     gate at both callsites)
//   • the old `every(v === 0)` guard is REMOVED. Portica §3.3: once the
//     series are real, a genuinely flat-at-zero month is a legitimate
//     measurement and keeps its line. Guard on emptiness, not on values.
export function finalizeSeries(points) {
  const kept = points.filter(p => p && p.value !== null && Number.isFinite(p.value));
  return kept;
}

// Styles
const scanCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '16px 18px',
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const heroCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '24px',
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const supportingCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '16px 18px',
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};

const exploreLinkStyle = {
  display: 'inline-block',
  marginTop: 10,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--color-action-primary)',
};

const skeletonCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: '18px 20px',
};

const shimmerBlock = {
  borderRadius: 4,
  background: 'linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
};


const metricSlicerStyle = {
  display: 'inline-flex',
  gap: 4,
  padding: 3,
  background: 'var(--neutral-100)',
  borderRadius: 'var(--btn-radius)',
};

const metricSlicerBtnStyle = {
  border: 'none',
  background: 'transparent',
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  borderRadius: 'var(--btn-radius)',
};

const metricSlicerBtnActiveStyle = {
  background: 'var(--color-surface)',
  color: 'var(--color-text-default)',
  fontWeight: 600,
};


const materialUsageCardStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: 'var(--card-radius)',
  padding: '18px 20px',
};
